import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { Bot, Send, Loader2, User, Sparkles } from "lucide-react";

const SUGGESTIONS = {
  agriculture: ["Explain the NDVI result", "Is chemical contamination a concern?", "How were the carbon credits calculated?"],
  earthquake: ["Why is this building at risk?", "Explain the TBDY 2018 flags", "Which recyclable materials should we use?"],
};

export const AskAnalyst = ({ projectId, scanId, domain }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const session = projectId;

  useEffect(() => {
    fetch(`${API}/chat/history?project_id=${projectId}&session_id=${session}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setMessages(data.map((m) => ({ role: m.role, content: m.content }))))
      .catch(() => {});
  }, [projectId, session]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: projectId, scan_id: scanId, message: q, session_id: session }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The Analyst is temporarily unavailable. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="qs-panel p-5" data-testid="ask-analyst">
      <div className="flex items-center gap-2 mb-4">
        <span className="grid place-items-center h-9 w-9 rounded-sm bg-gradient-to-br from-[#8A2BE2] to-[#00FFFF]">
          <Bot className="h-5 w-5 text-[#050A15]" />
        </span>
        <div>
          <h4 className="font-display font-semibold leading-tight">Ask the Analyst</h4>
          <p className="text-[11px] text-slate-500 font-mono">Claude Sonnet 5 · streaming</p>
        </div>
      </div>

      <div ref={scrollRef} className="max-h-80 overflow-y-auto space-y-4 pr-1" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-sm text-slate-500">
            <p className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[#00FFFF]" /> Ask anything about this scan, indices or on-chain records.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {(SUGGESTIONS[domain] || []).map((s) => (
                <button key={s} data-testid="chat-suggestion" onClick={() => send(s)}
                  className="text-xs border border-white/10 px-3 py-1.5 hover:border-[#00FFFF]/50 hover:text-[#00FFFF] transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={`${i}-${m.role}`} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <span className={`grid place-items-center h-7 w-7 rounded-sm shrink-0 ${m.role === "user" ? "bg-[#00FFFF]/15" : "bg-[#8A2BE2]/15"}`}>
              {m.role === "user" ? <User className="h-4 w-4 text-[#00FFFF]" /> : <Bot className="h-4 w-4 text-[#8A2BE2]" />}
            </span>
            <div className={`text-sm leading-relaxed whitespace-pre-line px-3.5 py-2.5 max-w-[85%] ${m.role === "user" ? "bg-[#00FFFF]/10 text-slate-100" : "bg-[#111B3D] text-slate-300"}`}>
              {m.content || <Loader2 className="h-4 w-4 animate-spin text-[#8A2BE2]" />}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 mt-4">
        <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about NDVI, risk, carbon credits…"
          className="flex-1 bg-[#050A15] border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-[#8A2BE2] transition-colors" />
        <button data-testid="chat-send" type="submit" disabled={streaming || !input.trim()}
          className="qs-cta bg-[#8A2BE2] text-white px-4 flex items-center disabled:opacity-50">
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};
